import React, { useEffect, useState, useRef, lazy, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BuildingIcon, UsersIcon, SparklesIcon, BellIcon, CreditCardIcon, ShieldAlertIcon, CheckCircle2Icon, PlusIcon, TrashIcon, ShieldIcon, UploadIcon, ExternalLinkIcon, MapPinIcon, UserIcon, ShieldCheckIcon, CameraIcon, FileCheckIcon, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { useBusiness, BUSINESS_TYPE_LABEL } from './BusinessContext';
import { PremiumSelect } from './PremiumSelect';
import { InviteMemberModal, TeamMember, ROLE_PRESETS, accessSummary } from './InviteMemberModal';
import { UpgradePlanModal } from './UpgradePlanModal';
const INITIAL_MEMBERS: TeamMember[] = [{
  name: 'Fadi',
  role: 'Admin',
  email: 'fadi@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Admin')!.access,
  canViewContact: true
}, {
  name: 'Sara',
  role: 'Sales',
  email: 'sara@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Sales')!.access,
  canViewContact: true
}, {
  name: 'Sales Team',
  role: 'Viewer',
  email: 'team@email.com',
  access: ROLE_PRESETS.find(r => r.id === 'Viewer')!.access,
  canViewContact: false
}];
type Section = 'Business Profile' | 'Team Members' | 'Notifications' | 'Plan & Billing' | 'Danger Zone';
export function BusinessSettings() {
  const {
    hub,
    currentPlanId,
    listingCount,
    PACKAGES,
    updateHubProfile,
    subscribed,
    cancelSubscription
  } = useBusiness();
  const [section, setSection] = useState<Section>('Business Profile');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNote, setCancelNote] = useState('');
  const [logo, setLogo] = useState(hub?.logo || '');
  const [location, setLocation] = useState('Dubai, UAE');
  const [mapPin, setMapPin] = useState('');
  const [sellerType, setSellerType] = useState<'personal' | 'business'>('business');
  const [idVerified, setIdVerified] = useState(false);
  const [tradeLicenseUploaded, setTradeLicenseUploaded] = useState(false);
  const [members, setMembers] = useState<TeamMember[]>(INITIAL_MEMBERS);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeInitialPlan, setUpgradeInitialPlan] = useState<string | undefined>();
  const currentPlan = PACKAGES.find(p => p.id === currentPlanId) || PACKAGES[0];
  const paidPackages = PACKAGES.filter(p => p.id !== 'Free');
  // Shop handle is auto-derived from the name and not editable.
  const handleSlug = (hub?.name ?? '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'yourname';
  const openUpgrade = (planId?: string) => {
    setUpgradeInitialPlan(planId);
    setUpgradeOpen(true);
  };
  const openInvite = () => {
    setEditingMember(null);
    setInviteOpen(true);
  };
  const openEdit = (m: TeamMember) => {
    setEditingMember(m);
    setInviteOpen(true);
  };
  const saveMember = (m: TeamMember) => {
    setMembers(prev => {
      const idx = prev.findIndex(x => x.email === m.email && x.email !== '');
      if (idx >= 0) return prev.map((x, i) => i === idx ? m : x);
      return [...prev, m];
    });
    setInviteOpen(false);
  };
  const removeMember = (email: string) => setMembers(prev => prev.filter(x => x.email !== email));
  const sections: {
    id: Section;
    icon: React.ReactNode;
  }[] = [{
    id: 'Business Profile',
    icon: <BuildingIcon className="w-4 h-4" data-id="element-732" />
  }, {
    id: 'Team Members',
    icon: <UsersIcon className="w-4 h-4" data-id="element-733" />
  }, {
    id: 'Notifications',
    icon: <BellIcon className="w-4 h-4" data-id="element-734" />
  }, {
    id: 'Plan & Billing',
    icon: <CreditCardIcon className="w-4 h-4" data-id="element-735" />
  }, {
    id: 'Danger Zone',
    icon: <ShieldAlertIcon className="w-4 h-4" data-id="element-736" />
  }];
  return <div className="w-full max-w-6xl mx-auto pb-24" data-id="element-737">
      <div className="mb-6" data-id="element-738">
        <h2 className="text-2xl font-display font-bold text-dark-navy" data-id="element-739">
          Settings
        </h2>
        <p className="text-sm text-slate-500" data-id="element-740">
          Manage your Business Hub profile, team, and preferences.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-741">
        {/* Section nav */}
        <div className="lg:w-56 flex-shrink-0" data-id="element-742">
          <div className="flex lg:flex-col gap-1 overflow-x-auto no-scrollbar" data-id="element-743">
            {sections.map(s => <button key={s.id} onClick={() => setSection(s.id)} className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${section === s.id ? 'bg-white text-primary-indigo shadow-soft border border-slate-100' : 'text-slate-600 hover:bg-white/60'} ${s.id === 'Danger Zone' && section !== s.id ? 'text-red-600' : ''}`} data-id="element-744">
                <span className={section === s.id ? 'text-primary-indigo' : s.id === 'Danger Zone' ? 'text-red-500' : 'text-slate-400'} data-id="element-745">
                  {s.icon}
                </span>
                {s.id}
              </button>)}
          </div>
        </div>

        {/* Content */}
        <motion.div key={section} initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} className="flex-1 space-y-6" data-id="element-746">
          {section === 'Business Profile' && <Card data-id="element-747">
              <SectionTitle title="Business Profile" desc="This information appears across TALAB." data-id="element-748" />
              <div className="space-y-6" data-id="element-749">
                {/* Step 1 — choose seller type first (drives the name field below) */}
                <div data-id="element-750">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-751">
                    I'm selling as
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-752">
                    {([{
                  id: 'personal',
                  label: 'Personal Seller',
                  desc: 'Selling as an individual',
                  icon: UserIcon
                }, {
                  id: 'business',
                  label: 'Business',
                  desc: 'Registered company or shop',
                  icon: BuildingIcon
                }] as const).map(opt => {
                  const active = sellerType === opt.id;
                  return <button key={opt.id} type="button" onClick={() => setSellerType(opt.id)} className={`text-left p-4 rounded-xl border-2 transition-all flex items-start gap-3 ${active ? 'border-primary-indigo bg-primary-indigo/5' : 'border-slate-200 hover:border-slate-300 bg-white'}`} data-id="element-753">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${active ? 'bg-primary-indigo text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-754">
                            <opt.icon className="w-5 h-5" data-id="element-755" />
                          </div>
                          <div data-id="element-756">
                            <p className="font-semibold text-slate-900 text-sm" data-id="element-757">
                              {opt.label}
                            </p>
                            <p className="text-xs text-slate-500 mt-0.5" data-id="element-758">
                              {opt.desc}
                            </p>
                          </div>
                        </button>;
                })}
                  </div>
                </div>

                <div className="flex items-start gap-6" data-id="element-759">
                  <div className="flex-shrink-0" data-id="element-760">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-761">
                      {sellerType === 'business' ? 'Shop Logo' : 'Profile Photo'}
                    </label>
                    <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center relative overflow-hidden group cursor-pointer hover:border-primary-indigo/50 hover:bg-indigo-50/30 transition-colors" data-id="element-762">
                      {logo ? <img src={logo} alt="Shop Logo" className="w-full h-full object-cover" data-id="element-763" /> : <>
                          <UploadIcon className="w-6 h-6 text-slate-400 mb-1 group-hover:text-primary-indigo" data-id="element-764" />
                          <span className="text-[10px] font-medium text-slate-500 group-hover:text-primary-indigo" data-id="element-765">
                            Upload
                          </span>
                        </>}
                      <input type="file" className="absolute inset-0 opacity-0 cursor-pointer" accept="image/*" onChange={e => {
                    if (e.target.files && e.target.files[0]) {
                      const url = URL.createObjectURL(e.target.files[0]);
                      setLogo(url);
                    }
                  }} data-id="element-766" />
                    </div>
                  </div>
                  <div className="flex-1 space-y-4" data-id="element-767">
                    {sellerType === 'business' ? <div data-id="element-768">
                        <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-769">
                          Business Name
                        </label>
                        <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm ${tradeLicenseUploaded ? 'bg-slate-50 border-slate-200 text-slate-900 font-medium' : 'bg-slate-50 border-slate-200 border-dashed text-slate-400'}`} data-id="element-770">
                          {tradeLicenseUploaded ? <>
                              <FileCheckIcon className="w-4 h-4 text-success shrink-0" data-id="element-771" />
                              <span className="flex-1 truncate" data-id="element-772">
                                {hub?.name}
                              </span>
                              <span className="text-[11px] font-medium text-slate-400 bg-white border border-slate-200 px-2 py-0.5 rounded-full" data-id="element-773">
                                From trade license
                              </span>
                            </> : <>
                              <UploadIcon className="w-4 h-4 shrink-0" data-id="element-774" />
                              <span data-id="element-775">
                                Upload your trade license to auto-fill the
                                business name
                              </span>
                            </>}
                        </div>
                      </div> : <Field label="Full Name" defaultValue={hub?.name ?? ''} onChange={val => updateHubProfile({
                  name: val
                })} data-id="element-776" />}
                    <div data-id="element-777">
                      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-778">
                        Shop Handle
                      </label>
                      <div className="flex items-center rounded-xl border border-slate-200 bg-slate-100 overflow-hidden" data-id="element-779">
                        <span className="px-4 py-3 text-slate-500 text-sm font-medium" data-id="element-780">
                          talab.app/
                        </span>
                        <span className="flex-1 px-1 py-3 text-slate-700 text-sm font-semibold truncate" data-id="element-781">
                          {handleSlug}
                        </span>
                      </div>
                      <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400" data-id="element-782">
                        <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-783" />
                        Auto-generated from your name and can't be changed.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Verification */}
                <div className="rounded-2xl border border-slate-200 p-5 space-y-5" data-id="element-784">
                  {/* Account verification — required for both */}
                  <div data-id="element-785">
                    <div className="flex items-center justify-between mb-2" data-id="element-786">
                      <label className="block text-sm font-medium text-slate-700" data-id="element-787">
                        Account Verification
                      </label>
                      <span className="text-xs font-medium text-error" data-id="element-788">
                        Required
                      </span>
                    </div>
                    {idVerified ? <div className="flex items-center gap-3 p-4 bg-success/5 border border-success/20 rounded-xl" data-id="element-789">
                        <CheckCircle2Icon className="w-6 h-6 text-success shrink-0" data-id="element-790" />
                        <div className="flex-1" data-id="element-791">
                          <p className="font-medium text-slate-900 text-sm" data-id="element-792">
                            ID submitted for verification
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-793">
                            We'll review and confirm your identity shortly.
                          </p>
                        </div>
                        <button type="button" onClick={() => setIdVerified(false)} className="text-xs font-medium text-slate-500 hover:text-slate-700" data-id="element-794">
                          Replace
                        </button>
                      </div> : <div className="rounded-xl border border-slate-200 border-dashed p-4" data-id="element-795">
                        <div className="flex items-center gap-3 mb-3" data-id="element-796">
                          <ShieldCheckIcon className="w-5 h-5 text-primary-indigo" data-id="element-797" />
                          <p className="text-sm text-slate-600" data-id="element-798">
                            Verify your identity by scanning or uploading your
                            ID.
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3" data-id="element-799">
                          <button type="button" onClick={() => setIdVerified(true)} className="flex flex-col items-center justify-center p-4 bg-slate-50 border border-slate-200 rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group" data-id="element-800">
                            <CameraIcon className="w-6 h-6 text-slate-400 group-hover:text-primary-indigo mb-1.5 transition-colors" data-id="element-801" />
                            <span className="text-sm font-medium text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-802">
                              Scan ID
                            </span>
                          </button>
                          <button type="button" onClick={() => setIdVerified(true)} className="flex flex-col items-center justify-center p-4 bg-slate-50 border border-slate-200 rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group" data-id="element-803">
                            <UploadIcon className="w-6 h-6 text-slate-400 group-hover:text-primary-indigo mb-1.5 transition-colors" data-id="element-804" />
                            <span className="text-sm font-medium text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-805">
                              Upload ID
                            </span>
                          </button>
                        </div>
                      </div>}
                  </div>

                  {/* Trade license — business only */}
                  {sellerType === 'business' && <div data-id="element-806">
                      <div className="flex items-center justify-between mb-2" data-id="element-807">
                        <label className="block text-sm font-medium text-slate-700" data-id="element-808">
                          Trade License
                        </label>
                        <span className="text-xs font-medium text-error" data-id="element-809">
                          Required for business
                        </span>
                      </div>
                      {tradeLicenseUploaded ? <div className="flex items-center gap-3 p-4 bg-success/5 border border-success/20 rounded-xl" data-id="element-810">
                          <FileCheckIcon className="w-6 h-6 text-success shrink-0" data-id="element-811" />
                          <div className="flex-1" data-id="element-812">
                            <p className="font-medium text-slate-900 text-sm" data-id="element-813">
                              Trade license uploaded
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-814">
                              Pending review by the Talab team.
                            </p>
                          </div>
                          <button type="button" onClick={() => setTradeLicenseUploaded(false)} className="text-xs font-medium text-slate-500 hover:text-slate-700" data-id="element-815">
                            Replace
                          </button>
                        </div> : <button type="button" onClick={() => {
                  setTradeLicenseUploaded(true);
                  // Trade license is the source of truth for the legal
                  // business name — auto-fill it on upload.
                  updateHubProfile({
                    name: 'Easylease Rentals LLC'
                  });
                }} className="w-full flex items-center gap-3 p-4 bg-slate-50 border border-slate-200 border-dashed rounded-xl hover:border-primary-indigo hover:bg-primary-indigo/5 transition-all group text-left" data-id="element-816">
                          <UploadIcon className="w-6 h-6 text-slate-400 group-hover:text-primary-indigo transition-colors shrink-0" data-id="element-817" />
                          <div data-id="element-818">
                            <span className="block text-sm font-medium text-slate-700 group-hover:text-primary-indigo transition-colors" data-id="element-819">
                              Upload a valid trade license
                            </span>
                            <span className="block text-xs text-slate-400 mt-0.5" data-id="element-820">
                              PDF or image of your active UAE trade license —
                              we'll read the business name from it
                            </span>
                          </div>
                        </button>}
                    </div>}

                  {/* Verification status summary */}
                  <div className={`flex items-center gap-2 text-sm rounded-xl px-4 py-3 ${idVerified && (sellerType === 'personal' || tradeLicenseUploaded) ? 'bg-success/5 text-success' : 'bg-amber-50 text-amber-700'}`} data-id="element-821">
                    {idVerified && (sellerType === 'personal' || tradeLicenseUploaded) ? <>
                        <CheckCircle2Icon className="w-4 h-4" data-id="element-822" />
                        <span className="font-medium" data-id="element-823">
                          Verification complete — your account is fully set up.
                        </span>
                      </> : <>
                        <ShieldAlertIcon className="w-4 h-4" data-id="element-824" />
                        <span className="font-medium" data-id="element-825">
                          {sellerType === 'business' ? 'Verify your ID and upload your trade license to activate selling.' : 'Verify your ID to activate selling.'}
                        </span>
                      </>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-826">
                  <PremiumSelect label="Business Type" value={BUSINESS_TYPE_LABEL[hub?.type ?? 'generic']} options={Object.values(BUSINESS_TYPE_LABEL)} data-id="element-827" />
                  <div data-id="element-828">
                    <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-829">
                      Location Name
                    </label>
                    <input value={location} onChange={e => setLocation(e.target.value)} placeholder="e.g. Al Quoz, Dubai, UAE" className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-sm" data-id="element-830" />
                  </div>
                </div>

                {/* Map pin */}
                <div data-id="element-831">
                  <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-832">
                    Pin Location on Google Maps
                  </label>
                  <div className="flex flex-col sm:flex-row gap-2" data-id="element-833">
                    <div className="relative flex-1" data-id="element-834">
                      <MapPinIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-835" />
                      <input value={mapPin} onChange={e => setMapPin(e.target.value)} placeholder="Paste a Google Maps link or coordinates (e.g. 25.1193, 55.2010)" className="w-full pl-9 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-sm" data-id="element-836" />
                    </div>
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapPin || location || 'Dubai, UAE')}`} target="_blank" rel="noreferrer" className="px-4 py-3 bg-white border border-slate-200 text-slate-700 text-sm font-medium rounded-xl hover:border-primary-indigo hover:text-primary-indigo transition-colors flex items-center justify-center gap-2 whitespace-nowrap" data-id="element-837">
                      <ExternalLinkIcon className="w-4 h-4" data-id="element-838" /> Open Google Maps
                    </a>
                  </div>
                  <p className="text-xs text-slate-400 mt-1.5" data-id="element-839">
                    Drop a pin in Google Maps, then paste the link or
                    coordinates here. The preview below updates automatically.
                  </p>

                  {/* Map preview */}
                  <div className="mt-3 rounded-2xl overflow-hidden border border-slate-200 shadow-soft" data-id="element-840">
                    <iframe title="Business location preview" className="w-full h-64" loading="lazy" referrerPolicy="no-referrer-when-downgrade" src={`https://www.google.com/maps?q=${encodeURIComponent(mapPin || location || 'Dubai, UAE')}&output=embed`} data-id="element-841" />
                  </div>
                </div>
                <Field label="Contact Email" defaultValue="business@email.com" data-id="element-842" />
                <Field label="Phone" defaultValue="+971 4 000 0000" data-id="element-843" />
                <TextAreaField label="About" defaultValue="Premium vehicle rentals across the UAE." data-id="element-844" />
              </div>
              <SaveBar onSave={() => updateHubProfile({
            logo,
            shopSlug: handleSlug
          })} data-id="element-845" />
            </Card>}

          {section === 'Team Members' && <Card data-id="element-846">
              <div className="flex items-center justify-between mb-5" data-id="element-847">
                <SectionTitle title="Team Members" desc="Invite and manage who can access this hub." noMargin data-id="element-848" />
                <button onClick={openInvite} className="px-4 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2" data-id="element-849">
                  <PlusIcon className="w-4 h-4" data-id="element-850" /> Invite
                </button>
              </div>
              <div className="space-y-3" data-id="element-851">
                {members.map(m => <div key={m.email || m.name} className="flex items-center justify-between bg-slate-50 border border-slate-100 rounded-xl px-4 py-3" data-id="element-852">
                    <div className="flex items-center gap-3" data-id="element-853">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple text-white font-bold flex items-center justify-center text-sm" data-id="element-854">
                        {m.name.charAt(0)}
                      </div>
                      <div data-id="element-855">
                        <div className="font-medium text-dark-navy text-sm" data-id="element-856">
                          {m.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-857">{m.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-858">
                      <span className="text-xs font-medium text-slate-600 px-2 py-1 bg-white border border-slate-200 rounded-md" data-id="element-859">
                        {m.role}
                      </span>
                      <span className="hidden sm:inline text-xs text-slate-400" data-id="element-860">
                        {accessSummary(m.access)}
                      </span>
                      <button onClick={() => openEdit(m)} className="p-1.5 text-slate-400 hover:text-primary-indigo hover:bg-indigo-50 rounded-md" title="Edit access" data-id="element-861">
                        <ShieldIcon className="w-4 h-4" data-id="element-862" />
                      </button>
                      {m.role !== 'Admin' && <button onClick={() => removeMember(m.email)} className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-md" title="Remove" data-id="element-863">
                          <TrashIcon className="w-4 h-4" data-id="element-864" />
                        </button>}
                    </div>
                  </div>)}
              </div>
            </Card>}

          {section === 'Notifications' && <Card data-id="element-865">
              <SectionTitle title="Notifications" desc="Choose what you want to be alerted about." data-id="element-866" />
              <div className="space-y-1" data-id="element-867">
                <Toggle label="New leads" desc="When Talab qualifies a new buyer." on data-id="element-868" />
                <Toggle label="Hot leads (90%+ intent)" desc="High-intent buyers ready to purchase." on data-id="element-869" />
                <Toggle label="New messages" desc="Customer replies in Conversations." on data-id="element-870" />
                <Toggle label="Weekly demand report" desc="Search and interest signal summary." data-id="element-871" />
              </div>
            </Card>}

          {section === 'Plan & Billing' && <Card data-id="element-872">
              <SectionTitle title="Plan & Billing" desc="Manage your subscription and payment method." data-id="element-873" />
              <div className="bg-gradient-to-br from-indigo-900 to-purple-900 rounded-2xl p-6 text-white mb-8" data-id="element-874">
                <div className="flex items-center justify-between mb-4" data-id="element-875">
                  <div data-id="element-876">
                    <div className="text-sm text-indigo-200" data-id="element-877">Current Plan</div>
                    <div className="text-2xl font-display font-bold" data-id="element-878">
                      {currentPlan.name}
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-bold" data-id="element-879">
                    Active
                  </span>
                </div>

                <div className="mb-4" data-id="element-880">
                  <div className="flex items-center justify-between text-sm mb-1.5" data-id="element-881">
                    <span className="text-indigo-100" data-id="element-882">Listing Usage</span>
                    <span className="font-medium" data-id="element-883">
                      {listingCount} of{' '}
                      {currentPlan.listingLimit === Infinity ? 'Unlimited' : currentPlan.listingLimit}{' '}
                      listings
                    </span>
                  </div>
                  <div className="w-full h-2 bg-black/20 rounded-full overflow-hidden" data-id="element-884">
                    <div className="h-full bg-white/80 rounded-full transition-all" style={{
                  width: `${Math.min(100, listingCount / (currentPlan.listingLimit === Infinity ? Math.max(listingCount, 1) : currentPlan.listingLimit) * 100)}%`
                }} data-id="element-885" />
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm text-indigo-100" data-id="element-886">
                  <span data-id="element-887">
                    {currentPlan.price === 0 ? 'Free forever' : `AED ${currentPlan.price.toLocaleString()} / month · Renews soon`}
                  </span>
                  {currentPlan.price > 0 && <button className="text-white hover:text-indigo-200 underline underline-offset-2 transition-colors" data-id="element-888">
                      Update Payment Method
                    </button>}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50/60 px-5 py-4" data-id="element-889">
                <div data-id="element-890">
                  <div className="font-bold text-dark-navy" data-id="element-891">
                    Need more listings?
                  </div>
                  <p className="text-sm text-slate-500" data-id="element-892">
                    Compare plans and unlock a higher listing capacity.
                  </p>
                </div>
                <button onClick={() => openUpgrade()} className="px-5 py-2.5 bg-primary-indigo text-white text-sm font-bold rounded-xl hover:bg-indigo-700 transition-colors whitespace-nowrap" data-id="element-893">
                  {currentPlan.price > 0 ? 'Change Plan' : 'Upgrade Plan'}
                </button>
              </div>

              {(subscribed || currentPlan.price > 0) && <div className="mt-5 pt-5 border-t border-slate-100" data-id="element-894">
                  {!cancelOpen ? <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-id="element-895">
                      <div data-id="element-896">
                        <div className="font-medium text-dark-navy text-sm" data-id="element-897">
                          Cancel subscription
                        </div>
                        <p className="text-sm text-slate-500" data-id="element-898">
                          You'll keep {currentPlan.name} access until the end of
                          your billing period, then move to the Free plan.
                        </p>
                      </div>
                      <button onClick={() => setCancelOpen(true)} className="px-4 py-2 bg-white border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap" data-id="element-899">
                        Cancel subscription
                      </button>
                    </div> : <div className="rounded-2xl border border-red-100 bg-red-50 p-4" data-id="element-900">
                      <div className="font-bold text-red-900 text-sm mb-1" data-id="element-901">
                        Cancel your {currentPlan.name} plan?
                      </div>
                      <p className="text-sm text-red-700 mb-4" data-id="element-902">
                        Your listing limit will drop to the Free plan (1
                        listing). Listings beyond the free limit will be paused
                        until you upgrade again.
                      </p>

                      <div className="mb-4" data-id="element-903">
                        <label className="block text-sm font-medium text-red-900 mb-2" data-id="element-904">
                          Please tell us why you're leaving
                          <span className="text-red-500" data-id="element-905"> *</span>
                        </label>
                        <div className="space-y-2" data-id="element-906">
                          {['Too expensive', "I'm not using it enough", 'Missing features I need', 'Switching to another tool', 'Just a temporary pause', 'Other'].map(reason => <label key={reason} className={`flex items-center gap-2.5 px-3 py-2.5 rounded-xl border bg-white cursor-pointer transition-colors text-sm ${cancelReason === reason ? 'border-red-400 ring-1 ring-red-200' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-907">
                              <input type="radio" name="cancelReason" value={reason} checked={cancelReason === reason} onChange={e => setCancelReason(e.target.value)} className="accent-red-600" data-id="element-908" />
                              <span className="text-slate-700" data-id="element-909">{reason}</span>
                            </label>)}
                        </div>
                        {cancelReason === 'Other' && <textarea rows={2} value={cancelNote} onChange={e => setCancelNote(e.target.value)} placeholder="Tell us more…" className="mt-2 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-200 resize-none" data-id="element-910" />}
                        {cancelReason && cancelReason !== 'Other' && <textarea rows={2} value={cancelNote} onChange={e => setCancelNote(e.target.value)} placeholder="Anything else you'd like to add? (optional)" className="mt-2 w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-200 resize-none" data-id="element-911" />}
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2 sm:justify-end" data-id="element-912">
                        <button onClick={() => {
                  setCancelOpen(false);
                  setCancelReason('');
                  setCancelNote('');
                }} className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-white rounded-lg" data-id="element-913">
                          Keep my plan
                        </button>
                        <button disabled={!cancelReason || cancelReason === 'Other' && !cancelNote.trim()} onClick={() => {
                  cancelSubscription();
                  setCancelOpen(false);
                  setCancelReason('');
                  setCancelNote('');
                }} className="px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-914">
                          Submit & cancel
                        </button>
                      </div>
                    </div>}
                </div>}
            </Card>}

          {section === 'Danger Zone' && <div className="bg-red-50 border border-red-100 rounded-2xl p-6 space-y-4" data-id="element-915">
              <SectionTitle title="Danger Zone" desc="These actions are permanent." noMargin danger data-id="element-916" />
              <div className="flex items-center justify-between pt-2" data-id="element-917">
                <div data-id="element-918">
                  <div className="font-medium text-red-900" data-id="element-919">
                    Deactivate Business Hub
                  </div>
                  <div className="text-sm text-red-700" data-id="element-920">
                    Temporarily hide your hub and listings.
                  </div>
                </div>
                <button className="px-4 py-2 bg-white border border-red-200 text-red-700 text-sm font-medium rounded-lg hover:bg-red-50" data-id="element-921">
                  Deactivate
                </button>
              </div>
              <div className="h-px bg-red-100" data-id="element-922" />
              <div className="flex items-center justify-between" data-id="element-923">
                <div data-id="element-924">
                  <div className="font-medium text-red-900" data-id="element-925">
                    Delete Business Hub
                  </div>
                  <div className="text-sm text-red-700" data-id="element-926">
                    Permanently remove your hub, catalog, and leads.
                  </div>
                </div>
                <button className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 flex items-center gap-2" data-id="element-927">
                  <TrashIcon className="w-4 h-4" data-id="element-928" /> Delete
                </button>
              </div>
            </div>}
        </motion.div>
      </div>

      {inviteOpen && <InviteMemberModal member={editingMember} onClose={() => setInviteOpen(false)} onSave={saveMember} data-id="element-929" />}

      {upgradeOpen && <UpgradePlanModal initialPlan={upgradeInitialPlan} onClose={() => setUpgradeOpen(false)} data-id="element-930" />}
    </div>;
}
function Card({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-soft" data-id="element-931">
      {children}
    </div>;
}
function SectionTitle({
  title,
  desc,
  noMargin,
  danger
}: {
  title: string;
  desc: string;
  noMargin?: boolean;
  danger?: boolean;
}) {
  return <div className={noMargin ? '' : 'mb-5'} data-id="element-932">
      <h3 className={`text-lg font-bold ${danger ? 'text-red-900' : 'text-dark-navy'}`} data-id="element-933">
        {title}
      </h3>
      <p className={`text-sm ${danger ? 'text-red-700' : 'text-slate-500'}`} data-id="element-934">
        {desc}
      </p>
    </div>;
}
function Field({
  label,
  defaultValue,
  onChange
}: {
  label: string;
  defaultValue?: string;
  onChange?: (val: string) => void;
}) {
  return <div data-id="element-935">
      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-936">
        {label}
      </label>
      <input defaultValue={defaultValue} onChange={e => onChange?.(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo text-sm" data-id="element-937" />
    </div>;
}
function TextAreaField({
  label,
  defaultValue
}: {
  label: string;
  defaultValue?: string;
}) {
  return <div data-id="element-938">
      <label className="block text-sm font-medium text-slate-700 mb-2" data-id="element-939">
        {label}
      </label>
      <textarea rows={3} defaultValue={defaultValue} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 text-sm resize-none" data-id="element-940" />
    </div>;
}
function SaveBar({
  onSave
}: {
  onSave?: () => void;
}) {
  return <div className="flex justify-end gap-3 pt-5 mt-5 border-t border-slate-100" data-id="element-941">
      <button className="px-4 py-2 text-slate-600 text-sm font-medium hover:bg-slate-50 rounded-lg" data-id="element-942">
        Cancel
      </button>
      <button onClick={onSave} className="px-5 py-2 bg-primary-indigo text-white text-sm font-medium rounded-lg hover:bg-indigo-700 flex items-center gap-2" data-id="element-943">
        <CheckCircle2Icon className="w-4 h-4" data-id="element-944" /> Save Changes
      </button>
    </div>;
}
function Toggle({
  label,
  desc,
  on
}: {
  label: string;
  desc: string;
  on?: boolean;
}) {
  const [enabled, setEnabled] = useState(!!on);
  return <div className="flex items-center justify-between py-3 border-b border-slate-50 last:border-0" data-id="element-945">
      <div className="pr-4" data-id="element-946">
        <div className="font-medium text-dark-navy text-sm" data-id="element-947">{label}</div>
        <div className="text-xs text-slate-500" data-id="element-948">{desc}</div>
      </div>
      <button onClick={() => setEnabled(e => !e)} role="switch" aria-checked={enabled} aria-label={label} className={`relative w-11 h-6 rounded-full flex-shrink-0 transition-colors ${enabled ? 'bg-primary-indigo' : 'bg-slate-200'}`} data-id="element-949">
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${enabled ? 'translate-x-5' : ''}`} data-id="element-950" />
      </button>
    </div>;
}